import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, CalendarDays, ChevronDown, ClipboardCheck, FileText, HardHat, PenTool, Search } from "lucide-react";
import { PROCESS, PROCESS_PRINCIPLES } from "../../data/content";

const EASE = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };
const PROCESS_ICONS = [Search, PenTool, CalendarDays, HardHat, ClipboardCheck, FileText];

const HERO_SLIDES = [
  "/design-interior-lab.jpg",
  "/design-cleanroom.jpg",
  "/design-piping.jpg",
  "/design-hvac.jpg",
  "/design-cadbim.jpg",
];

const ProcessLine = ({ children, delay, className = "" }) => (
  <motion.span
    initial="hidden"
    whileInView="show"
    viewport={VIEWPORT}
    className={`block overflow-hidden ${className}`}
  >
    <motion.span
      variants={{
        hidden: { y: "105%" },
        show: { y: 0, transition: { duration: 0.58, delay, ease: EASE } },
      }}
      className="block"
    >
      {children}
    </motion.span>
  </motion.span>
);

const ProcessBlueprint = () => (
  <svg
    aria-hidden="true"
    data-testid="process-blueprint"
    className="absolute inset-0 w-full h-full text-[#172A3A] opacity-[0.06] pointer-events-none"
    viewBox="0 0 1200 900"
    preserveAspectRatio="none"
  >
    <defs>
      <pattern id="process-blueprint-grid" width="72" height="72" patternUnits="userSpaceOnUse">
        <path d="M 72 0 L 0 0 0 72" fill="none" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="1200" height="900" fill="url(#process-blueprint-grid)" />
    <g fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M120 700 H350 V560 H520 V700 H790 V500 H1080" />
      <path d="M170 250 H430 V370 H690 V250 H990" />
      <path d="M790 700 L990 500 M520 700 L790 500" />
      <circle cx="350" cy="560" r="36" />
      <circle cx="990" cy="250" r="48" />
      <path d="M950 250 H1030 M990 210 V290" />
    </g>
  </svg>
);

const stageVariants = {
  hidden: { opacity: 0, y: 22 },
  show: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.58, delay, ease: EASE },
  }),
  hover: { y: -4, transition: { duration: 0.25, ease: "easeOut" } },
};

const StageDetails = ({ p }) => (
  <motion.div
    initial={{ height: 0, opacity: 0 }}
    animate={{ height: "auto", opacity: 1 }}
    exit={{ height: 0, opacity: 0 }}
    transition={{ duration: 0.4, ease: EASE }}
    className="overflow-hidden"
  >
    <div className="pt-4 mt-4 border-t border-[#E5E8EA]" data-testid={`process-details-${p.num}`}>
      <p className="font-inter text-[#59636D] text-[13px] leading-[1.65]">{p.desc}</p>
      <ul className="mt-4 space-y-2.5">
        {p.details.map((d) => (
          <li key={d} className="font-inter text-[#18232D] text-[12px] leading-relaxed flex items-start gap-2">
            <span className="w-1.5 h-1.5 bg-[#F47721] mt-1.5 shrink-0" />
            {d}
          </li>
        ))}
      </ul>
    </div>
  </motion.div>
);

const ProcessStage = ({ p, i, open, onToggle }) => {
  const Icon = PROCESS_ICONS[i];
  const delay = 0.95 + i * 0.13;
  const activeText = open ? "text-[#F47721]" : "text-[#98A1AA] group-hover:text-[#F47721]";
  const activeIcon = open ? "text-[#F47721] border-[#F47721]" : "text-[#7B8692] border-[#DDE2E6] group-hover:text-[#F47721] group-hover:border-[#F47721]";
  const activeTitle = open ? "text-[#172A3A]" : "text-[#59636D] group-hover:text-[#172A3A]";
  const activeDesc = open ? "text-[#4B5560]" : "text-[#76808A] group-hover:text-[#4B5560]";

  return (
    <motion.article
      data-testid={`process-stage-${p.num}`}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      aria-label={`${p.title} process stage`}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
      variants={stageVariants}
      custom={delay}
      whileHover="hover"
      className="group relative w-full flex gap-5 text-left outline-none focus-visible:ring-2 focus-visible:ring-[#F47721] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F7F4] lg:block lg:h-full"
    >
      <span className={`hidden lg:block absolute left-0 right-0 top-[31px] h-[2px] transition-colors duration-300 ${open ? "bg-[#F47721]" : "bg-transparent group-hover:bg-[#F47721]"}`} />
      <span className="relative z-10 w-16 shrink-0 flex flex-col items-center lg:w-auto lg:block">
        <span className={`w-16 h-16 rounded-full bg-white border flex items-center justify-center transition-colors duration-300 lg:mx-auto ${activeIcon}`}>
          <Icon size={23} strokeWidth={1.5} />
        </span>
      </span>
      <div className={`flex-1 bg-white border rounded-md p-5 mb-5 lg:mb-0 lg:mt-6 lg:min-h-[250px] lg:h-full flex flex-col transition-colors duration-300 ${open ? "border-[#F47721]/55" : "border-[#E5E8EA] group-hover:border-[#F47721]/45"}`}>
        <div className="flex items-start justify-between gap-3">
          <span className={`font-manrope text-[22px] font-bold tracking-tight transition-colors duration-300 ${activeText}`}>{p.num}</span>
          <span className="font-manrope text-[10px] font-bold uppercase tracking-[0.18em] text-[#F47721]">{p.keyword}</span>
        </div>
        <h3 className={`font-manrope font-bold uppercase tracking-tight text-[15px] leading-[1.25] mt-4 transition-colors duration-300 ${activeTitle}`}>
          {p.title}
        </h3>
        <p className={`font-inter text-[13px] leading-[1.6] mt-3 transition-colors duration-300 ${activeDesc}`}>{p.short}</p>
        <div className="mt-5 lg:mt-auto flex items-center justify-between border-t border-[#EEF0F1] pt-4" data-testid={`process-toggle-${p.num}`}>
          <span className="font-manrope text-[10px] font-bold uppercase tracking-[0.18em] text-[#59636D]">
            {open ? "Close Stage" : "View Stage"}
          </span>
          <ChevronDown size={16} className={`text-[#F47721] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </div>
        <AnimatePresence initial={false}>{open && <StageDetails p={p} />}</AnimatePresence>
      </div>
    </motion.article>
  );
};

export const Process = () => {
  const [activeStage, setActiveStage] = useState(null);
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setBgIndex((i) => (i + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="process" data-testid="process-section" className="relative overflow-hidden bg-[#172A3A]">
      <div className="relative min-h-[62vh] lg:min-h-[68vh] flex items-center overflow-hidden" data-testid="process-hero">
        <AnimatePresence initial={false}>
          <motion.img
            key={bgIndex}
            src={HERO_SLIDES[bgIndex]}
            alt="VR Vision Projects interiors, laboratory, cleanroom and piping work showcase"
            data-testid="process-hero-bg"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ x: "14%", opacity: 0, scale: 1.05 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ x: "-14%", opacity: 0, scale: 1.03 }}
            transition={{ duration: 1.15, ease: EASE }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-[#172A3A]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#172A3A]/80 via-[#172A3A]/40 to-[#172A3A]/10" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10 w-full py-24 lg:py-28">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.45, delay: 0.5, ease: EASE }}
            className="font-manrope text-[11px] font-bold uppercase tracking-[0.34em] text-[#F47721]"
            data-testid="process-label"
          >
            Our Process
          </motion.p>
          <h2 className="font-manrope font-bold text-white leading-[1.06] tracking-tight text-[40px] md:text-[52px] lg:text-[62px] mt-6 max-w-5xl" data-testid="process-heading">
            <ProcessLine delay={1.0}>FROM REQUIREMENT TO DELIVERY.</ProcessLine>
            <ProcessLine delay={1.2} className="text-[#F47721]">A STRUCTURED APPROACH TO EVERY PROJECT.</ProcessLine>
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.55, delay: 1.82, ease: EASE }}
            className="font-inter font-medium text-[17px] leading-[1.6] text-slate-200 max-w-[680px] mt-7"
            data-testid="process-description"
          >
            A disciplined project delivery framework that brings together engineering, coordination, execution and quality at every stage.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.45, delay: 2.35, ease: EASE }}
            className="mt-10 flex items-center gap-3 text-slate-300"
            data-testid="process-scroll-indicator"
          >
            <span className="font-inter text-[11px] uppercase tracking-[0.25em]">Explore The Journey</span>
            <ArrowDown size={15} aria-hidden="true" />
          </motion.div>
        </div>
      </div>

      <div className="relative bg-[#F7F7F4] border-t border-[#E5E8EA] overflow-hidden">
        <ProcessBlueprint />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10 py-24 lg:py-28">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.5, ease: EASE }}
            className="font-manrope text-[11px] font-bold uppercase tracking-[0.3em] text-[#F47721]"
            data-testid="process-journey-label"
          >
            Process Journey
          </motion.p>

          <motion.div
            className="relative mt-14"
            data-testid="process-timeline"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-40px" }}
          >
            <div aria-hidden="true" className="hidden lg:block absolute left-[8.333%] right-[8.333%] top-[31px] h-[2px] bg-[#E5E8EA]" />
            <motion.div
              aria-hidden="true"
              data-testid="process-timeline-line"
              className="hidden lg:block absolute left-[8.333%] right-[8.333%] top-[31px] h-[2px] origin-left bg-[#F47721]"
              variants={{
                hidden: { scaleX: 0 },
                show: { scaleX: 1, transition: { duration: 0.9, delay: 0.15, ease: EASE } },
              }}
            />
            <motion.div
              aria-hidden="true"
              data-testid="process-mobile-timeline-line"
              className="lg:hidden absolute left-[31px] top-8 bottom-8 w-[2px] origin-top bg-[#F47721]/70"
              variants={{
                hidden: { scaleY: 0 },
                show: { scaleY: 1, transition: { duration: 0.8, delay: 0.15, ease: EASE } },
              }}
            />

            <div className="grid grid-cols-1 lg:grid-cols-6 gap-0 lg:gap-5 relative">
              {PROCESS.map((p, i) => (
                <ProcessStage key={p.num} p={p} i={i} open={activeStage === i} onToggle={() => setActiveStage(activeStage === i ? null : i)} />
              ))}
            </div>
          </motion.div>

          <div className="mt-20 lg:mt-24 border-t border-[#E5E8EA] pt-14" data-testid="process-statement">
            <ProcessLine delay={0.05} className="font-manrope font-bold tracking-tight leading-[1.08] text-[34px] md:text-[42px] text-[#18232D]">
              BUILT AROUND CONTROL.
            </ProcessLine>
            <ProcessLine delay={0.18} className="font-manrope font-bold tracking-tight leading-[1.08] text-[34px] md:text-[42px] text-[#F47721]">
              DELIVERED WITH DISCIPLINE.
            </ProcessLine>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={VIEWPORT}
              transition={{ duration: 0.55, delay: 0.32, ease: EASE }}
              className="font-inter text-[#59636D] text-base md:text-[17px] leading-[1.65] mt-6 max-w-3xl"
              data-testid="process-statement-description"
            >
              Our approach combines structured planning, engineering coordination, execution control and performance verification to maintain consistency from project initiation through completion.
            </motion.p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="process-principles">
            {PROCESS_PRINCIPLES.map((p, i) => (
              <motion.div
                key={p.title}
                data-testid={`process-principle-${i + 1}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={VIEWPORT}
                transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
                className="bg-white border border-[#E5E8EA] rounded-md p-5 relative overflow-hidden"
              >
                <span className="absolute top-0 left-0 h-[2px] w-10 bg-[#F47721]" />
                <h4 className="font-manrope font-bold uppercase tracking-[0.08em] text-[13px] leading-snug text-[#18232D]">{p.title}</h4>
                <p className="font-inter text-[#59636D] text-[13px] leading-[1.6] mt-3">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

